import * as React from "react";
import {
  Editor,
  EditorState,
  ContentState,
  getDefaultKeyBinding,
  SelectionState,
  Modifier,
  EditorChangeType,
  DraftHandleValue,
} from "draft-js";
import { useTranslation } from "react-i18next";
import { EmojiPicker, getEmojiFromNative } from "../../Emoji";
import { useOnClickOutside } from "../../../hooks/useOnClickOutside";
import { EmojiData, BaseEmoji } from "emoji-mart";
import "./input.css";
import { useEditorState } from "../EditorState";
import { findWithRegex, unicodeRegex } from "../utils";
import { useRegisterPopup } from "../../Register";
import { useGuestId } from "../../../hooks/useGuestId";
import emojiIcon from "../assets/emojiIcon.svg";
import giftIcon from "../assets/giftIcon.svg";
import sendIcon from "../assets/sendIcon.svg";
import { useCustomChatContext } from "../../UserProvider";

const NEWLINE_REGEX = /\n/g;
const SAVE_EVENT_NAME = "myeditor-save";

const myKeyBindingFn = (e: React.KeyboardEvent<{}>): string | null => {
  if (e.keyCode === 13) return SAVE_EVENT_NAME;
  return getDefaultKeyBinding(e);
};

const getStateWithMaxLengthEnsured = (
  oldState: EditorState,
  newState: EditorState,
  maxLength: number
): EditorState => {
  const contentState = newState.getCurrentContent();
  const oldContent = oldState.getCurrentContent();
  if (
    contentState === oldContent ||
    contentState.getPlainText().length <= maxLength
  ) {
    return newState;
  } else {
    return EditorState.undo(
      EditorState.push(
        oldState,
        ContentState.createFromText(oldContent.getPlainText()),
        "delete-character"
      )
    );
  }
};

const isBaseEmoji = (data: EmojiData): data is BaseEmoji => {
  return (data as BaseEmoji).native !== undefined;
};

const addEmoji = (editorState: EditorState, e: BaseEmoji) => {
  const emoji = e.native;
  const contentState = editorState.getCurrentContent();
  const contentStateWithEntity = contentState.createEntity(
    "emoji",
    "IMMUTABLE",
    { emoji: e }
  );
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  const currentSelectionState = editorState.getSelection();

  let emojiAddedContent;
  let emojiEndPos: number;
  let blockSize: number;

  const afterRemovalContentState = Modifier.removeRange(
    contentState,
    currentSelectionState,
    "backward"
  );

  // deciding on the position to insert emoji
  const targetSelection = afterRemovalContentState.getSelectionAfter();

  emojiAddedContent = Modifier.insertText(
    afterRemovalContentState,
    targetSelection,
    emoji,
    undefined,
    entityKey
  );

  emojiEndPos = targetSelection.getAnchorOffset();
  const blockKey = targetSelection.getAnchorKey();
  blockSize = contentState.getBlockForKey(blockKey).getLength();

  // If the emoji is inserted at the end, a space is appended right after for
  // a smooth writing experience.
  if (emojiEndPos === blockSize) {
    emojiAddedContent = Modifier.insertText(
      emojiAddedContent,
      emojiAddedContent.getSelectionAfter(),
      " "
    );
  }

  const newEditorState = EditorState.push(
    editorState,
    emojiAddedContent,
    "insert-emoji" as EditorChangeType
  );

  return EditorState.forceSelection(
    newEditorState,
    emojiAddedContent.getSelectionAfter()
  );
};

const removeNewLines = (editorState: EditorState): EditorState => {
  const contentState = editorState.getCurrentContent();
  const blocks = contentState.getBlockMap();
  let newContentState = contentState;

  blocks.forEach((block) => {
    if (block === undefined) return;
    const plainText = block.getText();

    const clearNewLines = (start: number, end: number) => {
      const selection = SelectionState.createEmpty(block.getKey())
        .set("anchorOffset", start)
        .set("focusOffset", end);
      const textToReplace = plainText.substring(start, end);
      newContentState = Modifier.replaceText(
        newContentState,
        selection as SelectionState,
        textToReplace.replace(/\n/g, " ")
      );
    };

    findWithRegex(NEWLINE_REGEX, block, clearNewLines);
  });

  if (!newContentState.equals(contentState)) {
    return EditorState.push(
      editorState,
      newContentState,
      "convert-to-immutable-emojis" as EditorChangeType
    );
  }

  return editorState;
};

const attachImmutableEntitiesToEmojis = (
  editorState: EditorState
): EditorState => {
  const contentState = editorState.getCurrentContent();
  const blocks = contentState.getBlockMap();
  let newContentState = contentState;

  blocks.forEach((block) => {
    if (block === undefined) return;
    const plainText = block.getText();

    const addEntityToEmoji = (start: number, end: number) => {
      const existingEntityKey = block.getEntityAt(start);
      if (existingEntityKey) {
        // avoid manipulation in case the emoji already has an entity
        const entity = newContentState.getEntity(existingEntityKey);
        if (entity && entity.getType() === "emoji") {
          return;
        }
      }

      const selection = SelectionState.createEmpty(block.getKey())
        .set("anchorOffset", start)
        .set("focusOffset", end);
      const emojiText = plainText.substring(start, end);
      const contentStateWithEntity = newContentState.createEntity(
        "emoji",
        "IMMUTABLE",
        { emoji: getEmojiFromNative(emojiText) }
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      newContentState = Modifier.replaceText(
        newContentState,
        selection as SelectionState,
        emojiText,
        undefined,
        entityKey
      );
    };

    findWithRegex(unicodeRegex, block, addEntityToEmoji);
  });

  if (!newContentState.equals(contentState)) {
    return EditorState.push(
      editorState,
      newContentState,
      "convert-to-immutable-emojis" as EditorChangeType
    );
  }

  return editorState;
};

const MessageInput = React.memo(
  React.forwardRef<
    Editor,
    {
      maxLength: number;
      onInput: (input: string) => void;
      chatId?: string;
      onSendGiftOpen: (val: boolean) => void;
      isSendGiftOpen: boolean;
    }
  >(({ maxLength, onInput, chatId, onSendGiftOpen, isSendGiftOpen }, ref) => {
    const { t } = useTranslation();
    const emojiRef = React.useRef<HTMLDivElement>(null);
    const giftRef = React.useRef<HTMLDivElement>(null);
    const [showEmojiSelector, setShowEmojiSelector] = React.useState(false);
    const [editorState, setEditorState] = useEditorState();
    const registration = useRegisterPopup();
    const [, , , getIsLoggedAsGuest] = useGuestId();
    const { reportedMessage } = useCustomChatContext();

    useOnClickOutside(
      emojiRef,
      React.useCallback(() => setShowEmojiSelector(false), [])
    );
    useOnClickOutside(
      giftRef,
      React.useCallback(() => onSendGiftOpen(false), [])
    );

    const onChange = React.useCallback(
      (state: EditorState) => {
        let newEditorState = attachImmutableEntitiesToEmojis(
          removeNewLines(state)
        );

        if (
          !newEditorState.getCurrentContent().equals(state.getCurrentContent())
        ) {
          // Forcing the current selection ensures that it will be at it's right place.
          // This solves the issue where inserting an Emoji on OSX with Apple's Emoji
          // selector led to the right selection the data, but wrong position in
          // the contenteditable.
          newEditorState = EditorState.forceSelection(
            newEditorState,
            newEditorState.getSelection()
          );
        }
        setEditorState((editorState) =>
          getStateWithMaxLengthEnsured(editorState, newEditorState, maxLength)
        );
      },
      [maxLength, setEditorState]
    );

    const onCommand = React.useCallback(
      (command: string) => {
        if (command === SAVE_EVENT_NAME) {
          if (getIsLoggedAsGuest()) {
            registration.openRegisterPopup();
            return "not-handled";
          }

          let tmpEditorState = editorState;
          let contentState = tmpEditorState.getCurrentContent();
          const text = contentState.getPlainText();
          if (!/\S/.test(text)) return "not-handled";
          const firstBlock = contentState.getFirstBlock();
          const lastBlock = contentState.getLastBlock();
          const allSelected = new SelectionState({
            anchorKey: firstBlock.getKey(),
            anchorOffset: 0,
            focusKey: lastBlock.getKey(),
            focusOffset: lastBlock.getLength(),
            hasFocus: true,
          });
          contentState = Modifier.removeRange(
            contentState,
            allSelected,
            "backward"
          );
          tmpEditorState = EditorState.push(
            tmpEditorState,
            contentState,
            "remove-range"
          );
          tmpEditorState = EditorState.forceSelection(
            tmpEditorState,
            contentState.getSelectionAfter()
          );
          setEditorState(tmpEditorState);
          onInput(text);
          return "handled";
        }
        return "not-handled";
      },
      [getIsLoggedAsGuest, registration, editorState, onInput, setEditorState]
    );

    const onEmoji = React.useCallback(
      (emoji: EmojiData) => {
        if (!isBaseEmoji(emoji)) return;
        setEditorState((editorState) =>
          getStateWithMaxLengthEnsured(
            editorState,
            addEmoji(editorState, emoji),
            maxLength
          )
        );
      },
      [maxLength, setEditorState]
    );

    const onPaste: (
      text: string | null
    ) => DraftHandleValue = React.useCallback(
      (text: string | null) => {
        if (text !== null) {
          const finalText = text.replace(/\n/g, " ");
          setEditorState((editorState) => {
            const newContent = Modifier.replaceText(
              editorState.getCurrentContent(),
              editorState.getSelection(),
              finalText
            );
            return EditorState.push(
              editorState,
              newContent,
              "insert-characters"
            );
          });
        }
        return "handled";
      },
      [setEditorState]
    );

    return (
      <div className="relative">
        {reportedMessage && (
          <div
            style={{
              position: "absolute",
              width: "100%",
              top: "-50px",
              left: 0,
              border: "2px solid #196fff",
              background: "#064ec7",
              borderRadius: "6px",
              padding: "4px 14px",
              color: "#ffffff",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            {t("reported_message")}
          </div>
        )}
        <div
          className="flex items-center -mx-2"
          style={{
            border: "2px solid #196fff",
            background: "#196fff",
            borderRadius: "20px",
            padding: "7px 4px",
            color: "#73beea",
            fontSize: "16px",
          }}
        >
          <div className="border-gray-300 flex-shrink-0" ref={emojiRef}>
            <button
              onClick={() => setShowEmojiSelector((prev) => !prev)}
              className="flex items-center"
            >
              <img src={emojiIcon} alt="" />
            </button>
            {showEmojiSelector && (
              <div
                className="absolute"
                style={{ bottom: "40px", left: 0, width: "inherit" }}
              >
                <EmojiPicker onSelect={onEmoji} />
              </div>
            )}
          </div>
          <div className="border-gray-300 flex-shrink-0 pl-2" ref={giftRef}>
            <button
              onClick={() => onSendGiftOpen(!isSendGiftOpen)}
              className="flex items-center"
            >
              <img src={giftIcon} alt="" />
            </button>
          </div>
          <div className="flex-1 text-sm px-2 overflow-hidden">
            <Editor
              placeholder={t("type_a_message")}
              ref={ref}
              editorState={editorState}
              onChange={onChange}
              keyBindingFn={myKeyBindingFn}
              handleKeyCommand={onCommand}
              handlePastedText={onPaste}
            />
          </div>
          <div className="border-gray-300 flex-shrink-0 pl-2">
            <button
              onClick={() => {
                onCommand("myeditor-save");
              }}
              className="flex items-center"
            >
              <img src={sendIcon} alt="" />
            </button>
          </div>
        </div>
      </div>
    );
  })
);

export default MessageInput;
